import { Injectable } from '@angular/core';
import {
  StorageFile,
  StorageSelectorFilter,
  StorageSelectorType,
  StorageUtilsService,
} from './storage-utils.service';

import { ModalService } from 'src/app/components/modals/modal.service';
import { ClipboardService } from 'src/app/shared/services/clipboard.service';
import { FolderSelectorModalComponent } from '../components/folder-selector-modal/folder-selector-modal.component';
import { NewFolderModalComponent } from '../components/new-folder-modal/new-folder-modal.component';
import { RenameFileModalComponent } from '../components/rename-file-modal/rename-file-modal.component';
import { StorageModalComponent } from '../components/storage-modal/storage-modal.component';
import { ListItem } from 'src/app/components/scrolling-list';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  constructor(
    private modalService: ModalService,
    private storageUtilsService: StorageUtilsService,
    private clipboardService: ClipboardService,
  ) {}

  private _isFilteredFile(
    file: ListItem<StorageFile>,
    selectorFilter: StorageSelectorFilter,
  ) {
    if (this.storageUtilsService.fileIsFolder(file.item)) {
      return false;
    }

    let filtered = false;

    if (selectorFilter.extensions) {
      filtered = !this.storageUtilsService.fileHasExtension(
        file.item,
        selectorFilter.extensions,
      );
    }

    return filtered;
  }

  isMultipleSelector(storageFull: boolean, selectorType: StorageSelectorType) {
    return (
      storageFull ||
      selectorType === StorageUtilsService.SELECTOR_TYPES.MULTIPLE_FILE ||
      selectorType === StorageUtilsService.SELECTOR_TYPES.MULTIPLE_FILES_FOLDERS
    );
  }

  isFileSelector(storageFull: boolean, selectorType: StorageSelectorType) {
    return (
      storageFull ||
      selectorType === StorageUtilsService.SELECTOR_TYPES.SINGLE_FILE ||
      selectorType === StorageUtilsService.SELECTOR_TYPES.MULTIPLE_FILE ||
      selectorType === StorageUtilsService.SELECTOR_TYPES.MULTIPLE_FILES_FOLDERS
    );
  }

  isFolderSelector(storageFull: boolean, selectorType: StorageSelectorType) {
    return (
      storageFull ||
      selectorType === StorageUtilsService.SELECTOR_TYPES.SINGLE_FOLDER ||
      selectorType === StorageUtilsService.SELECTOR_TYPES.MULTIPLE_FILES_FOLDERS
    );
  }

  isMultipleFilesFoldersSelector(selectorType: StorageSelectorType) {
    return (
      selectorType === StorageUtilsService.SELECTOR_TYPES.MULTIPLE_FILES_FOLDERS
    );
  }

  isFolderFilter(selectorFilter: StorageSelectorFilter) {
    return (
      selectorFilter.name === StorageUtilsService.SELECTOR_FILTERS.FOLDERS.name
    );
  }

  canSelect(
    file: ListItem<StorageFile>,
    storageFull: boolean,
    selectorType: StorageSelectorType,
    selectorFilter: StorageSelectorFilter,
  ) {
    return (
      !this.storageUtilsService.fileIsTrash(file.item) &&
      !(file.item as StorageFile).isThrottled &&
      ((this.storageUtilsService.fileIsFolder(file.item) &&
        this.isFolderSelector(storageFull, selectorType)) ||
        (!this.storageUtilsService.fileIsFolder(file.item) &&
          this.isFileSelector(storageFull, selectorType))) &&
      !this._isFilteredFile(file, selectorFilter)
    );
  }

  isDisabled(
    file: ListItem<StorageFile>,
    storageFull: boolean,
    selectorType: StorageSelectorType,
    selectorFilter: StorageSelectorFilter,
  ) {
    return (
      this._isFilteredFile(file, selectorFilter) ||
      (!this.storageUtilsService.fileIsFolder(file.item) &&
        !this.isFileSelector(storageFull, selectorType))
    );
  }

  showAddFolderModal(folderPath: string, existingFiles: any[]): Promise<any> {
    return this.modalService.showMediumDialog(
      NewFolderModalComponent,
      {
        folderPath,
        existingFiles,
      },
      null,
      'more-actions-button',
    );
  }

  copyFileUrl(file: ListItem<StorageFile>): Promise<void> {
    return this.clipboardService.copy(this._buildFileUrl(file));
  }

  private _buildFileUrl(file: ListItem<StorageFile>): string {
    return file.item.kind === 'folder'
      ? this.storageUtilsService.getFolderSelfLinkUrl() +
          this._encodeURIKeepForwardSlash(file.item.name)
      : StorageUtilsService.STORAGE_FILE_URL +
          this.storageUtilsService.getBucketName() +
          '/' +
          this._encodeURIKeepForwardSlash(file.item.name);
  }

  private _encodeURIKeepForwardSlash(uri: string): string {
    return encodeURIComponent(uri).split('%2F').join('/');
  }

  showRenameFileModal(selectedFile: ListItem<StorageFile>): Promise<any> {
    return this.modalService.showMediumDialog(
      RenameFileModalComponent,
      {
        selectedFile,
      },
      null,
      'more-actions-button',
    );
  }

  showStorageModal(
    selectorType: StorageSelectorType = 'single-file',
    selectorFilter: StorageSelectorFilter = StorageUtilsService.SELECTOR_FILTERS
      .ALL,
    enableByURL: boolean = false,
  ): Promise<any> {
    return this.modalService.showLargeDialog(
      StorageModalComponent,
      {
        selectorType,
        selectorFilter,
        enableByURL,
      },
      null,
      'more-actions-button',
    );
  }

  showFolderSelectorModal(
    folderPath: string,
    excludedFiles: string[],
  ): Promise<any> {
    return this.modalService.showMediumDialog(
      FolderSelectorModalComponent,
      {
        folderPath,
        isTrashFolder: this.storageUtilsService.isTrashFolder(folderPath),
        excludedFiles,
      },
      { ariaLabelledBy: 'title' },
      'more-actions-button',
    );
  }
}
